import { useState, useMemo } from "react";

import {
  CATEGORY_ACCENTS,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  HIDDEN_CATEGORIES,
  presetsByCategory,
  animationPresets,
} from "../animation/presets";
import { loadFavorites, toggleFavorite } from "../animation/favorites";

import { PresetPreview } from "./PresetPreview";

import "./TemplateGallery.scss";

import type { CSSProperties } from "react";

const hexToRgba = (hex: string, alpha: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

type GalleryTab = "all" | "favorites";

/** Kept across mounts: the gallery unmounts whenever Flipbook mode or another
 * sidebar tab is shown, and coming back to a reset filter feels like the app
 * forgot what you were doing. */
let lastTab: GalleryTab = "all";
let lastQuery = "";

const HeartIcon = ({ filled }: { filled: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const TABS = [
  {
    tab: "all" as const,
    label: "All",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3.5" y="3.5" width="7" height="7" rx="2" />
        <rect x="13.5" y="3.5" width="7" height="7" rx="2" />
        <rect x="3.5" y="13.5" width="7" height="7" rx="2" />
        <rect x="13.5" y="13.5" width="7" height="7" rx="2" />
      </svg>
    ),
  },
  {
    tab: "favorites" as const,
    label: "Favorites",
    icon: <HeartIcon filled={false} />,
  },
];

export type TemplateGalleryProps = {
  onDragStart: (presetId: string, e: React.DragEvent) => void;
  selectedPresetId?: string;
  onSelectPreset: (presetId: string) => void;
  disabled?: boolean;
  /** Rendered right after the search field, before the results/category
   * grid — lets the caller show selection-specific content (e.g. the
   * selected element's currently applied animations) without this component
   * needing to know anything about clips or elements. */
  topSlot?: React.ReactNode;
};

export const TemplateGallery = ({
  onDragStart,
  selectedPresetId,
  onSelectPreset,
  disabled = false,
  topSlot,
}: TemplateGalleryProps) => {
  const [searchQuery, setSearchQueryState] = useState(lastQuery);
  const [activeTab, setActiveTabState] = useState<GalleryTab>(lastTab);
  const setSearchQuery = (next: string) => {
    lastQuery = next;
    setSearchQueryState(next);
  };
  const setActiveTab = (next: GalleryTab) => {
    lastTab = next;
    setActiveTabState(next);
  };
  const [favorites, setFavorites] = useState<Set<string>>(loadFavorites);

  const onToggleFavorite = (presetId: string) => {
    // value form, not an updater — toggleFavorite persists to localStorage,
    // and React replays/double-invokes updaters in dev, which would apply
    // the toggle twice and flip the favorite straight back off.
    setFavorites(toggleFavorite(favorites, presetId));
  };

  const favoritePresets = useMemo(
    () =>
      Object.values(animationPresets).filter(
        (preset) =>
          favorites.has(preset.id) && !HIDDEN_CATEGORIES.has(preset.category),
      ),
    [favorites],
  );

  const filteredPresets = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      return null;
    }
    const pool =
      activeTab === "favorites"
        ? favoritePresets
        : Object.values(animationPresets).filter(
            (preset) => !HIDDEN_CATEGORIES.has(preset.category),
          );
    // match preset name OR its category label, so semantic searches like
    // "entrance", "attention" or "3d" surface every preset in that category.
    return pool.filter(
      (preset) =>
        preset.name.toLowerCase().includes(query) ||
        CATEGORY_LABELS[preset.category].toLowerCase().includes(query),
    );
  }, [searchQuery, activeTab, favoritePresets]);

  const renderPresetCard = (preset: typeof animationPresets[string]) => {
    const isFavorite = favorites.has(preset.id);
    return (
      <div
        key={preset.id}
        className={`template-gallery__item ${
          selectedPresetId === preset.id
            ? "template-gallery__item--selected"
            : ""
        }`}
        style={
          {
            "--card-accent": CATEGORY_ACCENTS[preset.category],
            "--card-accent-wash": hexToRgba(
              CATEGORY_ACCENTS[preset.category],
              0.14,
            ),
          } as CSSProperties
        }
        // always draggable: dropping a card on an element animates THAT
        // element, which is the whole point of the no-selection state —
        // `disabled` only means "clicking can't apply to a selection".
        draggable
        onDragStart={(e) => onDragStart(preset.id, e)}
        onClick={() => !disabled && onSelectPreset(preset.id)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            onSelectPreset(preset.id);
          }
        }}
      >
        <button
          type="button"
          className={`template-gallery__favorite ${
            isFavorite ? "template-gallery__favorite--active" : ""
          }`}
          aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
          aria-pressed={isFavorite}
          title={isFavorite ? "Remove from favorites" : "Add to favorites"}
          // reachable even while the gallery can't apply: favoriting has
          // nothing to do with what's selected on canvas
          draggable={false}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(preset.id);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          onDragStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <HeartIcon filled={isFavorite} />
        </button>
        <div className="template-gallery__preview">
          <PresetPreview preset={preset} />
        </div>
        <div className="template-gallery__info">
          <span className="template-gallery__name">{preset.name}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="template-gallery">
      <div className="template-gallery__header">
        <h3>Animation Templates</h3>
        {/* shared HIG segmented control (panelTokens.scss) — same one the
            Templates/Flipbook switch above uses; active segment comes off
            aria-selected, so no --active class of our own. */}
        <div
          className="template-gallery__tabs hig-segmented"
          role="tablist"
          aria-label="Template filters"
        >
          {TABS.map(({ tab, label, icon }) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              className="template-gallery__tab hig-segmented__item"
              onClick={() => setActiveTab(tab)}
            >
              {icon}
              <span className="template-gallery__tab-label">{label}</span>
            </button>
          ))}
        </div>
        <div className="template-gallery__search-field">
          <svg
            className="template-gallery__search-icon"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="9" cy="9" r="6" />
            <line x1="13.5" y1="13.5" x2="17" y2="17" />
          </svg>
          <input
            type="text"
            placeholder="Search animations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="template-gallery__search"
          />
          {searchQuery && (
            <button
              type="button"
              className="template-gallery__search-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* clicking a card can't apply with nothing selected — say so, and
          point at the path that does work without a selection */}
      {disabled && (
        <p className="template-gallery__hint">
          Select an element to apply — or drag a template onto one.
        </p>
      )}

      {topSlot}

      {filteredPresets ? (
        <div className="template-gallery__search-results">
          {filteredPresets.length === 0 ? (
            <p className="template-gallery__no-results">
              No {activeTab === "favorites" ? "favorite " : ""}animations match
              "{searchQuery}"
            </p>
          ) : (
            <>
              <p className="template-gallery__result-count">
                {filteredPresets.length}{" "}
                {filteredPresets.length === 1 ? "result" : "results"}
              </p>
              <div className="template-gallery__grid">
                {filteredPresets.map(renderPresetCard)}
              </div>
            </>
          )}
        </div>
      ) : activeTab === "favorites" ? (
        favoritePresets.length === 0 ? (
          <div className="template-gallery__empty">
            <HeartIcon filled={false} />
            <p>No favorites yet</p>
            <span>Hover a template and tap its heart to pin it here.</span>
            <button
              type="button"
              className="template-gallery__empty-cta"
              onClick={() => setActiveTab("all")}
            >
              Browse all templates
            </button>
          </div>
        ) : (
          <div className="template-gallery__category">
            <div className="template-gallery__grid">
              {favoritePresets.map(renderPresetCard)}
            </div>
          </div>
        )
      ) : (
        CATEGORY_ORDER.map((category) => {
          const presets = presetsByCategory(category);
          return (
            <div key={category} className="template-gallery__category">
              <h4 className="template-gallery__category-title">
                {CATEGORY_LABELS[category]}
              </h4>
              <div className="template-gallery__grid">
                {presets.map(renderPresetCard)}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};
